@if (showCombinedChatPanel()) {
  <ng-container *ngTemplateOutlet="combinedChatPanel"></ng-container>
} @else {
  <ng-container *ngTemplateOutlet="legacyChatPanel"></ng-container>
}

<ng-template #combinedChatPanel>
  <ng-template #panelHeader>
    <div class="header">
      @if (showChatHistory() && !isExpanded()) {
        <div class="header-start-actions">
          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.ChevronLeft"
            [attr.aria-label]="'back' | translate"
            (click)="onToggleChatHistory()"
            data-test-id="back-to-chat-button"
            type="button"
          >
            {{ 'back' | translate }}
          </button>

          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.Plus"
            [attr.aria-label]="'new_chat' | translate"
            [attr.title]="'new_chat' | translate"
            (click)="onNewChat()"
            data-test-id="new-chat-button"
            type="button"
          >
            {{ 'new_chat' | translate }}
          </button>
        </div>
      } @else {
        <div class="header-start-actions">
          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.Clock"
            [attr.aria-label]="'chat_history' | translate"
            [attr.title]="'chat_history' | translate"
            [attr.aria-expanded]="isExpanded() ? showExpandedHistory() : showChatHistory()"
            aria-controls="ai-chat-history-panel"
            (click)="onChatsClick()"
            data-test-id="chat-history-button"
            type="button"
          >
            {{ 'chat_history' | translate }}
          </button>

          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.Plus"
            [attr.aria-label]="'new_chat' | translate"
            [attr.title]="'new_chat' | translate"
            (click)="onNewChat()"
            data-test-id="new-chat-button"
            type="button"
          >
            {{ 'new_chat' | translate }}
          </button>
        </div>
      }

      <div class="header-actions">
        @if ((!showChatHistory() || isExpanded()) && messages.length > 0) {
          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.TrashCan"
            [intent]="DsButtonIntent.destructive"
            [attr.aria-label]="'action_delete' | translate"
            [ds-tooltip]="'delete_chat' | translate"
            [tooltipPlacement]="DsTooltipPosition.bottom"
            (click)="onDeleteCurrentSession()"
            data-test-id="delete-chat-button"
            type="button"
          ></button>
        }

        @if (!isExpanded()) {
          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.Expand"
            [attr.aria-label]="'label_expand' | translate"
            [ds-tooltip]="'expand' | translate"
            [tooltipPlacement]="DsTooltipPosition.bottom"
            (click)="onExpand()"
            class="expand-button"
          ></button>
        } @else {
          <button
            ds-button
            [variant]="DsButtonVariants.microAction"
            [microActionIcon]="Icons.Minimize"
            [attr.aria-label]="'label_minimize' | translate"
            [ds-tooltip]="'label_minimize' | translate"
            [tooltipPlacement]="DsTooltipPosition.bottom"
            (click)="onCollapse()"
            class="collapse-button"
          ></button>
        }

        <button
          ds-button
          [variant]="DsButtonVariants.microAction"
          [microActionIcon]="Icons.XMark"
          [attr.aria-label]="'label_close' | translate"
          [ds-tooltip]="'label_close' | translate"
          [tooltipPlacement]="DsTooltipPosition.left"
          (click)="onClose()"
          class="close-button"
        ></button>
      </div>
    </div>
  </ng-template>

  @if (isExpanded() && showUxImprovements()) {
    <div class="chat-content side-by-side" [class.has-messages]="messages.length > 0">
      @if (showExpandedHistory()) {
        <div id="ai-chat-history-panel" class="history-sidebar ux-improvements">
          <app-ai-chat-history
            [isExpanded]="false"
            [activeSessionId]="sessionId"
            (sessionSelected)="onSessionSelected($event)"
            (sessionDeleted)="onSessionDeleted($event)"
          ></app-ai-chat-history>
        </div>
      }
      <div class="main-content ux-improvements">
        <ng-container *ngTemplateOutlet="panelHeader"></ng-container>
        <div class="chat-column" [class.landing]="messages.length === 0 && status === AiChatStatus.IDLE">
          <div class="panel-body" #panelBody>
            <app-ai-chat-messages
              [messages]="messages"
              [status]="status"
              [refreshSampleQuestionsRequest$]="refreshSampleQuestionsRequest$"
              [focusErrorRequest$]="focusErrorRequest$"
              [showUxImprovements]="true"
              (sampleQuestionClick)="onSampleQuestionClick($event)"
              (retry)="onRetry($event.message, $event.index)"
              (ratingChange)="onRatingChange($event.message, $event.rating)"
            ></app-ai-chat-messages>
          </div>
          <div class="panel-footer">
            <app-ai-chat-footer
              [status]="status"
              [messageValue]="messageValue"
              [focusInputRequest$]="focusInputRequest$"
              [clearInputRequest$]="clearInputRequest$"
              (sendMessage)="sendMessage($event)"
            ></app-ai-chat-footer>
          </div>
          @if (messages.length === 0 && status === AiChatStatus.IDLE) {
            <div class="panel-suggestions">
              <app-ai-chat-sample-questions
                count="3"
                (questionSelected)="onSampleQuestionClick($event)"
              ></app-ai-chat-sample-questions>
            </div>
          }
        </div>
      </div>
    </div>
  } @else if (isExpanded()) {
    <div class="chat-content side-by-side" [class.has-messages]="messages.length > 0">
      @if (showExpandedHistory()) {
        <div id="ai-chat-history-panel" class="history-sidebar">
          <app-ai-chat-history
            [isExpanded]="false"
            [activeSessionId]="sessionId"
            (sessionSelected)="onSessionSelected($event)"
            (sessionDeleted)="onSessionDeleted($event)"
          ></app-ai-chat-history>
        </div>
      }
      <div class="main-content">
        <ng-container *ngTemplateOutlet="panelHeader"></ng-container>
        <div class="panel-body" #panelBody>
          <app-ai-chat-messages
            [messages]="messages"
            [status]="status"
            [refreshSampleQuestionsRequest$]="refreshSampleQuestionsRequest$"
            [focusErrorRequest$]="focusErrorRequest$"
            (sampleQuestionClick)="onSampleQuestionClick($event)"
            (retry)="onRetry($event.message, $event.index)"
            (ratingChange)="onRatingChange($event.message, $event.rating)"
          ></app-ai-chat-messages>
        </div>
        <div class="panel-footer">
          <app-ai-chat-footer
            [status]="status"
            [messageValue]="messageValue"
            [focusInputRequest$]="focusInputRequest$"
            [clearInputRequest$]="clearInputRequest$"
            (sendMessage)="sendMessage($event)"
          ></app-ai-chat-footer>
        </div>
      </div>
    </div>
  } @else {
    <ng-container *ngTemplateOutlet="panelHeader"></ng-container>
    <div class="chat-content" [class.has-messages]="messages.length > 0">
      @if (showChatHistory()) {
        <div class="panel-body" #panelBody>
          <app-ai-chat-history
            [isExpanded]="isExpanded()"
            [activeSessionId]="sessionId"
            (sessionSelected)="onSessionSelected($event)"
            (sessionDeleted)="onSessionDeleted($event)"
          ></app-ai-chat-history>
        </div>
      } @else {
        <div class="panel-body" #panelBody>
          <app-ai-chat-messages
            [messages]="messages"
            [status]="status"
            [refreshSampleQuestionsRequest$]="refreshSampleQuestionsRequest$"
            [focusErrorRequest$]="focusErrorRequest$"
            (sampleQuestionClick)="onSampleQuestionClick($event)"
            (retry)="onRetry($event.message, $event.index)"
            (ratingChange)="onRatingChange($event.message, $event.rating)"
          ></app-ai-chat-messages>
        </div>
        <div class="panel-footer">
          <app-ai-chat-footer
            [status]="status"
            [messageValue]="messageValue"
            [focusInputRequest$]="focusInputRequest$"
            [clearInputRequest$]="clearInputRequest$"
            (sendMessage)="sendMessage($event)"
          ></app-ai-chat-footer>
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #legacyChatPanel>
  <div class="header">
    @if (showChatHistory()) {
      <button
        ds-button
        [variant]="DsButtonVariants.microAction"
        [microActionIcon]="Icons.ChevronLeft"
        [attr.aria-label]="'back' | translate"
        (click)="onToggleChatHistory()"
        data-test-id="back-to-chat-button"
        type="button"
      >
        {{ 'back' | translate }}
      </button>
    } @else {
      <h2 class="header-title">
        <ds-icon
          [icon]="Icons.Sparkles"
          [scale]="IconScale.FA_1_375X"
          [iconStyle]="IconStyles.Regular"
          [gradientColor]="DsGradients.Ai"
          class="header-icon"
        ></ds-icon>
        {{ 'ai_chat_header' | translate }}
      </h2>
    }

    <div class="header-actions">
      <button
        ds-button
        [variant]="DsButtonVariants.microAction"
        [microActionIcon]="Icons.Plus"
        [attr.aria-label]="'new_chat' | translate"
        [attr.title]="'new_chat' | translate"
        (click)="onNewChat()"
        data-test-id="new-chat-button"
        type="button"
      >
        {{ 'new_chat' | translate }}
      </button>

      @if (!showChatHistory()) {
        <button
          ds-button
          [variant]="DsButtonVariants.microAction"
          [microActionIcon]="Icons.Clock"
          [attr.aria-label]="'chat_history' | translate"
          [attr.title]="'chat_history' | translate"
          (click)="onToggleChatHistory()"
          data-test-id="chat-history-button"
          type="button"
        >
          {{ 'chat_history' | translate }}
        </button>
      }

      @if (!showChatHistory() && messages.length > 0) {
        <button
          ds-button
          [variant]="DsButtonVariants.microAction"
          [microActionIcon]="Icons.TrashCan"
          [intent]="DsButtonIntent.destructive"
          [attr.aria-label]="'action_delete' | translate"
          [ds-tooltip]="'delete_chat' | translate"
          [tooltipPlacement]="DsTooltipPosition.bottom"
          (click)="onDeleteCurrentSession()"
          data-test-id="delete-chat-button"
          type="button"
        ></button>
      }

      @if (!isExpanded()) {
        <button
          ds-button
          [variant]="DsButtonVariants.microAction"
          [microActionIcon]="Icons.Expand"
          [attr.aria-label]="'label_expand' | translate"
          [ds-tooltip]="'expand' | translate"
          [tooltipPlacement]="DsTooltipPosition.bottom"
          (click)="onExpand()"
          class="expand-button"
        ></button>
      } @else {
        <button
          ds-button
          [variant]="DsButtonVariants.microAction"
          [microActionIcon]="Icons.Minimize"
          [attr.aria-label]="'label_minimize' | translate"
          [ds-tooltip]="'label_minimize' | translate"
          [tooltipPlacement]="DsTooltipPosition.bottom"
          (click)="onCollapse()"
          class="collapse-button"
        ></button>
      }

      <button
        ds-button
        [variant]="DsButtonVariants.microAction"
        [microActionIcon]="Icons.XMark"
        [attr.aria-label]="'label_close' | translate"
        [ds-tooltip]="'label_close' | translate"
        [tooltipPlacement]="DsTooltipPosition.left"
        (click)="onClose()"
        class="close-button"
      ></button>
    </div>
  </div>
  <div class="chat-content" [class.has-messages]="messages.length > 0">
    @if (showChatHistory()) {
      <div class="panel-body" #panelBody>
        <app-ai-chat-history
          [isExpanded]="isExpanded()"
          [activeSessionId]="sessionId"
          (sessionSelected)="onSessionSelected($event)"
          (sessionDeleted)="onSessionDeleted($event)"
        ></app-ai-chat-history>
      </div>
    } @else {
      <div class="panel-body" #panelBody>
        <app-ai-chat-messages
          [messages]="messages"
          [status]="status"
          [refreshSampleQuestionsRequest$]="refreshSampleQuestionsRequest$"
          [focusErrorRequest$]="focusErrorRequest$"
          (sampleQuestionClick)="onSampleQuestionClick($event)"
          (retry)="onRetry($event.message, $event.index)"
          (ratingChange)="onRatingChange($event.message, $event.rating)"
        ></app-ai-chat-messages>
      </div>
      <div class="panel-footer">
        <app-ai-chat-footer
          [status]="status"
          [messageValue]="messageValue"
          [focusInputRequest$]="focusInputRequest$"
          [clearInputRequest$]="clearInputRequest$"
          (sendMessage)="sendMessage($event)"
        ></app-ai-chat-footer>
      </div>
    }
  </div>
</ng-template>
